// Browser-side preparation of the images sent with the PDF report request (the server renders the
// PDF, see http/deck_designer/useReport.ts).
import type { ReportLabel } from "../../../http/deckDesigner/types/report";
import { loadPublicImageAsBase64 } from "../../../utils";

// Page geometry of the server's layout, needed to orient captures for a full page.
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const PAGE_MARGIN_MM = 15;
const CONTENT_TOP_MM = PAGE_MARGIN_MM + 16;
// Content ends this far above the page's bottom edge, above the footer (pdfreport's footerReservedMm).
const FOOTER_RESERVED_MM = 20;

const fitWithinBounds = (
  imgWidth: number,
  imgHeight: number,
  maxWidth: number,
  maxHeight: number,
): [number, number] => {
  const ratio = imgWidth / imgHeight;
  let width = maxWidth;
  let height = maxWidth / ratio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * ratio;
  }
  return [width, height];
};

// The usable content box of a dedicated (no other content) A4 page, i.e. what a `fullPage` image
// has available - must match the server's layout (internal/export/pdfreport/doc.go).
const getPageContentSizeMm = (): { width: number; height: number } => ({
  width: A4_WIDTH_MM - 2 * PAGE_MARGIN_MM,
  height: A4_HEIGHT_MM - CONTENT_TOP_MM - FOOTER_RESERVED_MM,
});

// Rotates a canvas 90° clockwise onto a new one — done to the raster itself so a capture can be
// re-oriented to fill a page edge-to-edge instead of being letterboxed.
const rotateCanvas90 = (source: HTMLCanvasElement): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  canvas.width = source.height;
  canvas.height = source.width;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Canvas 2D context unavailable");
  }
  context.translate(canvas.width / 2, canvas.height / 2);
  context.rotate(Math.PI / 2);
  context.drawImage(source, -source.width / 2, -source.height / 2);
  return canvas;
};

// Rotates the capture 90° only if doing so lets it render bigger on a full dedicated page - e.g. a
// landscape capture (L1 > L2) benefits from becoming portrait to match the page, but a capture
// that's already portrait (L2 > L1) would only get smaller if rotated. Compares the fitted area in
// both orientations rather than assuming one is always better.
// Reports whether it rotated the capture, since a caller with its own normalized (fx, fy) label
// positions for the *unrotated* image needs to remap them - see remapLabelPositionForRotation. The
// source canvas is left as it is.
export const orientCanvasForFullPage = (canvas: HTMLCanvasElement): { canvas: HTMLCanvasElement; rotated: boolean } => {
  const { width: contentWidth, height: contentHeight } = getPageContentSizeMm();
  const [normalWidth, normalHeight] = fitWithinBounds(canvas.width, canvas.height, contentWidth, contentHeight);
  const [rotatedWidth, rotatedHeight] = fitWithinBounds(canvas.height, canvas.width, contentWidth, contentHeight);

  if (rotatedWidth * rotatedHeight > normalWidth * normalHeight) {
    return { canvas: rotateCanvas90(canvas), rotated: true };
  }
  return { canvas, rotated: false };
};

// The canvas as base64 PNG, without the "data:image/png;base64," prefix. Encoded asynchronously
// (toBlob), unlike toDataURL; empty, as toDataURL's, for a canvas with nothing to encode.
export const encodeCanvasAsPngBase64 = (canvas: HTMLCanvasElement): Promise<string> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve("");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.substring(dataUrl.indexOf(",") + 1));
      };
      reader.onerror = () => reject(new Error("Failed to encode the capture"));
      reader.readAsDataURL(blob);
    }, "image/png");
  });

// The report's static pictures (accessories, support family) as loadPublicImageAsBase64 gives them,
// fetched and encoded once per page load instead of on every export. A failed load isn't kept, so the
// next export tries again.
const publicImageCache = new Map<string, Promise<string | null>>();

export const loadCachedPublicImageAsBase64 = (url: string): Promise<string | null> => {
  let image = publicImageCache.get(url);

  if (!image) {
    image = loadPublicImageAsBase64(url).then((data) => {
      if (data == null) {
        publicImageCache.delete(url);
      }
      return data;
    });
    publicImageCache.set(url, image);
  }

  return image;
};

// A 90° clockwise raster rotation (see rotateCanvas90) maps a point at fractional position
// (fx, fy) in the original image to (1 - fy, fx) in the rotated one, and swaps which way a
// label is pushed to clear the line/edge it describes.
export const remapLabelPositionForRotation = (label: ReportLabel): ReportLabel => ({
  ...label,
  fx: 1 - label.fy,
  fy: label.fx,
  orientation: label.orientation === "vertical" ? "horizontal" : "vertical",
  // A line that was vertical (rotate: true) becomes horizontal once the image itself is rotated, and
  // vice versa - flip it along with orientation instead of just carrying the old value over.
  rotate: label.rotate === undefined ? undefined : !label.rotate,
});
